<template>
    <div>
        <b-modal
            id="assign-user-modal"
            title="Assign User"
            size="md"
            centered
            hide-footer
            @hidden="resetModal"
            >
            <b-form>

                <!-- Radio Selection -->
                <b-form-group>
                <b-form-radio-group v-model="selectedMode" name="userMode" class="d-flex justify-content-between">
                    <b-form-radio value="search">Search User</b-form-radio>
                    <b-form-radio value="create">Create User</b-form-radio>
                </b-form-radio-group>
                </b-form-group>

                <b-row>
                <b-col cols="6" v-if="selectedMode === 'create'">
                    <b-form-group label="Username">
                    <b-form-input v-model="createForm.username" placeholder="Enter username" />
                    </b-form-group>
                </b-col>
                <b-col cols="6" v-if="selectedMode === 'create'">
                    <b-form-group label="Password">
                    <b-form-input type="password" v-model="createForm.password" placeholder="Enter password" />
                    </b-form-group>
                </b-col>

                <b-col cols="12" v-if="selectedMode === 'search'">
                    <b-form-group label="Search Users">
                        <b-input-group>
                            <b-form-input
                                v-model="searchQuery"
                                placeholder="Search by email, name or profile ID..."
                                @keydown.enter.prevent="searchUsers"
                            />
                            <b-input-group-append>
                                <b-button 
                                    variant="primary" 
                                    @click="searchUsers"
                                    :disabled="!searchQuery.trim()"
                                >
                                    <span v-if="!loadingSearch">Search</span>
                                    <b-spinner v-else small />
                                </b-button>
                            </b-input-group-append>
                        </b-input-group>
                    </b-form-group>
                </b-col>
                </b-row>

                <!-- ✅ Search Mode - Results and Selected List -->
                <div v-if="selectedMode === 'search'">
                    <!-- Search Results Container with max-height -->
                    <div 
                        v-if="searchQuery && searchResults.length > 0" 
                        class="search-results-container"
                    >
                        <ul class="list-group">
                            <li
                                v-for="user in searchResults"
                                :key="user._id"
                                class="list-group-item"
                            >
                                <div class="d-flex justify-content-between align-items-center flex-wrap">
                                    <!-- User Info -->
                                    <div class="user-info">
                                        <strong>{{ user.screen_name || user.email || 'Unknown' }}</strong><br />
                                        <small class="text-muted">Serial: {{ user.serial_number || 'N/A' }}</small>
                                    </div>

                                    <!-- Action Buttons and Role Dropdown -->
                                    <div class="d-flex align-items-center action-section">
                                        <!-- Role Dropdown (shown only when user is selected) -->
                                        <div v-if="isUserSelected(user)" class="role-dropdown mr-2">
                                            <b-form-select
                                                v-model="selectedUsers.find(u => u._id === user._id).role"
                                                :options="roleOptions"
                                                size="sm"
                                                style="min-width: 140px;"
                                                required
                                                :class="{ 'is-invalid': !selectedUsers.find(u => u._id === user._id).role }"
                                            >
                                                <template #first>
                                                    <b-form-select-option :value="null" disabled>
                                                        Select role
                                                    </b-form-select-option>
                                                </template>
                                            </b-form-select>
                                            <div 
                                                v-if="!selectedUsers.find(u => u._id === user._id).role" 
                                                class="invalid-feedback d-block"
                                            >
                                                Please select a role
                                            </div>
                                        </div>

                                        <!-- Select/Remove Button -->
                                        <b-button
                                            size="sm"
                                            :variant="isUserSelected(user) ? 'danger' : 'primary'"
                                            @click="toggleUser(user)"
                                        >
                                            {{ isUserSelected(user) ? 'Remove' : 'Select' }}
                                        </b-button>
                                    </div>
                                </div>
                            </li>
                        </ul>
                    </div>

                    <!-- No Results Message -->
                    <div v-if="searchTriggered && searchResults.length === 0" class="text-center text-muted mt-3">
                        <p>No users found for "{{ searchQuery }}"</p>
                    </div>

                    <!-- Selected Users Count -->
                    <div v-if="selectedUsers.length > 0" class="mt-3">
                        <small class="text-success">
                            <strong>{{ selectedUsers.length }}</strong> user(s) selected
                        </small>
                    </div>
                </div>

                <!-- Save Button Section - Always visible at bottom -->
                <div class="save-button-section">
                    <b-button
                        v-if="selectedMode === 'search'"
                        variant="primary"
                        :disabled="!isFormValid() || loading || !areAllRolesSelected()"
                        @click="submitActionuser"
                    >
                        <b-spinner v-if="loading" small class="mr-1" />
                        <span v-if="!loading">Save</span>
                        <span v-else>Saving...</span>
                    </b-button>

                    <b-button
                        v-if="selectedMode === 'create'"
                        variant="primary"
                        :disabled="!isFormValid() || loading"
                        @click="submitAction"
                    >
                        <b-spinner v-if="loading" small class="mr-1" />
                        <span v-if="!loading">Save</span>
                        <span v-else>Saving...</span>
                    </b-button>

                    <b-button variant="outline-secondary" class="ml-2" @click="$bvModal.hide('assign-user-modal')">Cancel</b-button>
                </div>
            </b-form>
        </b-modal>
    </div>
</template>

<script>
import {
    BButton,
    BFormInput,
    BFormTextarea,
    BFormGroup,
    BAlert,
    BSpinner,
    BInputGroup,
    BInputGroupAppend,
    BFormCheckbox,
    BFormRadio,
    BFormRadioGroup,
    BForm,
    BCol,
    BRow,
    BFormSelect,
    BFormSelectOption,
} from "bootstrap-vue";
import axios from "axios";
import Swal from "sweetalert2/dist/sweetalert2.js";
import vSelect from "vue-select";
import ToastificationContent from "@core/components/toastification/ToastificationContent.vue";
import * as Helpers from '../../Helpers';
import flatPickr from 'vue-flatpickr-component'
import 'flatpickr/dist/flatpickr.css'
import moment from "moment";

export default {
    components: {
        BForm,
        BCol,
        BRow,
        BFormRadio,
        BFormRadioGroup,
        BButton,
        BFormInput,
        BFormTextarea,
        BFormGroup,
        BAlert,
        BSpinner,
        ToastificationContent,
        vSelect,
        flatPickr,
        BInputGroup,
        BInputGroupAppend,
        BFormCheckbox,
        BFormSelect,
        BFormSelectOption,
    },
    data() {
        return {
            loading: false,
            loadingSearch: false,
            selectedMode: "search",
            searchTriggered: false,
            hasError: false,
            errorMsg: "",
            createForm: {
                username: "",
                password: ""
            },
            searchQuery: "",
            searchResults: [],
            selectedUsers: [],
            roleOptions: [
                { value: 'student', text: 'Student' },
                { value: 'staff', text: 'Teacher / Staff' },
                { value: 'admin', text: 'School Admin' }
            ],
        };
    },
    computed: {
        filteredPlans() {
            if (!this.record.billing_cycle) {
                return this.get_plans;
            }
            return this.get_plans.filter(plan => plan.billing_cycle === this.record.billing_cycle);
        },
    },
    watch: {
        searchQuery(newVal) {
            if (!newVal.trim()) {
                this.searchResults = [];
                this.searchTriggered = false;
            }
        }
    },
    methods: {
        isFormValid() {
            if (this.selectedMode === 'create') {
                return this.createForm.username && this.createForm.password;
            }

            if (this.selectedMode === 'search') {
                return this.selectedUsers && this.selectedUsers.length > 0;
            }

            return false;
        },

        // Check if all selected users have a role selected
        areAllRolesSelected() {
            if (this.selectedUsers.length === 0) return false;
            
            return this.selectedUsers.every(user =>
                this.roleOptions.some(option => option.value === user.role)
            );
        },

        resetModal() {
            this.selectedMode = "search";
            this.createForm = { username: "", password: "" };
            this.searchQuery = "";
            this.searchResults = [];
            this.searchTriggered = false;
            this.selectedUsers = [];
            this.loadingSearch = false;
        },

        toggleUser(user) {
            const index = this.selectedUsers.findIndex(u => u._id === user._id);
            if (index === -1) {
                // Add user without default role (null initially)
                this.selectedUsers.push({
                    ...user,
                    role: null // Start with null to force selection
                });
            } else {
                this.selectedUsers.splice(index, 1);
            }
        },

        isUserSelected(user) {
            return this.selectedUsers.some(u => u._id === user._id);
        },

        async searchUsers() {
            const query = this.searchQuery.trim();
            
            if (!query) {
                this.searchResults = [];
                this.searchTriggered = false;
                return;
            }

            this.loadingSearch = true;
            this.searchTriggered = true;

            try {
                const response = await axios.post(
                    process.env.VUE_APP_API_URL + '/school/users/search',
                    { query: query },
                    {
                        headers: {
                            Authorization: 'Bearer ' + localStorage.getItem('_t'),
                        },
                    }
                );

                this.searchResults = Array.isArray(response.data.school) ? response.data.school : [];
            } catch (error) {
                console.error('Search failed:', error);
                this.searchResults = [];
                this.toastMessage = error.response?.data?.message || "Search failed";
                this.toastNotification("danger");
            } finally {
                this.loadingSearch = false;
            }
        },

        async submitActionuser() {
            // Check if all users have roles selected
            if (!this.areAllRolesSelected()) {
                Swal.fire({
                    title: "Missing Information",
                    text: "Please select a role for all selected users.",
                    icon: "warning",
                    confirmButtonColor: "#3085d6",
                });
                return;
            }

            const confirm = await Swal.fire({
                title: "Are you sure?",
                text: `Do you want to assign ${this.selectedUsers.length} user(s) with their respective roles?`,
                icon: "question",
                showCancelButton: true,
                confirmButtonColor: "#3085d6",
                cancelButtonColor: "#d33",
                confirmButtonText: "Yes, assign",
            });

            if (!confirm.isConfirmed) return;

            const schoolId = JSON.parse(localStorage.getItem('schoolid'));
            const token = localStorage.getItem('_t');

            this.loading = true;

            try {
                // Send each user with their role
                for (let user of this.selectedUsers) {
                    await axios.post(
                        process.env.VUE_APP_API_URL + '/school/assign-user',
                        {
                            school_id: schoolId,
                            user_id: user._id,
                            screen_id: user.screen_id,
                            role: user.role // Add role to the payload
                        },
                        {
                            headers: {
                                Authorization: 'Bearer ' + token,
                            },
                        }
                    );
                }

                this.toastMessage = "All users assigned successfully!";
                this.toastNotification("success");
                this.selectedUsers = [];
                this.$bvModal.hide("assign-user-modal");
                this.$emit("user-assigned-success");
            } catch (error) {
                console.error("Assignment failed:", error);
                this.toastMessage = error.response?.data?.message || "Some assignments failed.";
                this.toastNotification("danger");
            } finally {
                this.loading = false;
            }
        },

        toastNotification(variant) {
            this.$toast({
                component: ToastificationContent,
                props: {
                    title: "Notification",
                    icon: "BellIcon",
                    text: this.toastMessage,
                    variant,
                },
            });
        },

        async submitAction() {
            if (this.selectedMode === "create") {
                if (!this.createForm.username || !this.createForm.password) {
                    alert("Please enter username and password");
                    return;
                }
                const confirm = await Swal.fire({
                    title: "Are you sure?",
                    text: `Do you want to create user ?`,
                    icon: "question",
                    showCancelButton: true,
                    confirmButtonColor: "#3085d6",
                    cancelButtonColor: "#d33",
                    confirmButtonText: "Yes, create",
                });

                if (!confirm.isConfirmed) return;

                const schoolId = JSON.parse(localStorage.getItem("schoolid"));
                const payload = {
                    school_id: schoolId,
                    username: this.createForm.username,
                    password: this.createForm.password,
                    utmDetails: this.createForm.utmDetails ?? null,
                    installSectionId: this.createForm.installSectionId ?? null,
                };

                const headers = {
                    Authorization: "Bearer " + localStorage.getItem("_t"),
                    Platform: navigator.userAgent.includes("Android") ? "android" : "ios",
                    "Time-Zone": Intl.DateTimeFormat().resolvedOptions().timeZone,
                };

                axios
                    .post(
                        process.env.VUE_APP_API_URL + "/firebase/school-user/create",
                        payload,
                        { headers }
                    )
                    .then((response) => {
                        this.toastMessage = response.data.message || "User successfully created!";
                        this.toastNotification("success");
                        this.$bvModal.hide("assign-user-modal");
                        this.$emit("user-assigned-success");
                    })
                    .catch((error) => {
                        this.toastMessage =
                            error.response?.data?.message || error.message || "Error occurred";
                        this.toastNotification("danger");
                    });
            }
        },
    },
};
</script>

<style scoped>
.b-form-radio-group {
    gap: 1rem;
}

.list-group-item {
    padding: 1rem;
}

.user-info {
    flex: 1;
    min-width: 150px;
    margin-right: 15px;
}

.action-section {
    flex-shrink: 0;
    display: flex;
    align-items: center;
}

.role-dropdown {
    margin-right: 10px;
}

/* Scrollable search results container */
.search-results-container {
    max-height: 300px; /* Adjust this value as needed */
    overflow-y: auto;
    border: 1px solid #e9ecef;
    border-radius: 0.25rem;
    margin-top: 10px;
}

.search-results-container .list-group {
    margin-bottom: 0;
    border-radius: 0;
}

.search-results-container .list-group-item:first-child {
    border-top: none;
}

.search-results-container .list-group-item:last-child {
    border-bottom: none;
}

/* Save button section stays at bottom */
.save-button-section {
    margin-top: 20px;
    padding-top: 15px;
    border-top: 1px solid #e9ecef;
    text-align: right;
}

@media (max-width: 576px) {
    .list-group-item > div {
        flex-direction: column;
        align-items: flex-start !important;
    }
    
    .user-info {
        margin-right: 0;
        margin-bottom: 10px;
        width: 100%;
    }
    
    .action-section {
        width: 100%;
        justify-content: space-between;
        flex-wrap: wrap;
    }
    
    .role-dropdown {
        margin-right: 0;
        margin-bottom: 8px;
        width: 100%;
    }
    
    .search-results-container {
        max-height: 250px;
    }
}
</style>